/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import DeviceBrandsService from 'api/deviceBrands/DeviceBrandsService';
import DeviceModelsService from 'api/deviceModels/DeviceModelsService';
import { DeviceModelDto } from 'api/deviceModels/models/DeviceModelDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './DeviceModelScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import ReadOnlySelect from 'common/components/readOnlySelect/ReadOnlySelect';
import { SelectValueLabel } from 'common/types/SelectValueLabel';

type Props = {
};

const initialModel: DeviceModelDto = {
    id: '',
    name: '',
    deviceBrandId: '',
    hasCanReading: false,
    supportsIdentificationTag: false,
    rowVersion: ''
};

const DeviceModelScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const location = useLocation();
    const stateRow = (location.state as { row?: DeviceModelDto } | null)?.row;
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasDeviceModelsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CONFIG_DEVICE_BRAND_WRITE']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<DeviceModelDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<DeviceModelDto | undefined>(stateRow);
    const [brands, setBrands] = useState<SelectValueLabel[]>();
    const [refresh, setRefresh] = useState<number>(1);
    const [formData, setFormData] = useState<DeviceModelDto>(stateRow ?? initialModel);

    const getData = async () => {
        try {
            let result: DeviceModelDto = { ...initialModel };
            Loading.show();
            const brandsResult = await DeviceBrandsService.getDeviceBrands();
            setBrands(brandsResult);
            if (stateRow) {
                result = stateRow;
            } else if (id) {
                result = await DeviceModelsService.getById(id);
            }
            setItem(result);
            setFormData(result);
            setRefresh(refresh + 1);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get device model information', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/devicemodel/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/config-devices?tab=models');
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await DeviceModelsService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete device model', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: DeviceModelDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: DeviceModelDto = {
                id: formData.id,
                name: formData.name,
                deviceBrandId: formData.deviceBrandId,
                hasCanReading: formData.hasCanReading,
                supportsIdentificationTag: formData.supportsIdentificationTag,
                rowVersion: formData.rowVersion,
            };
            if (model && model.id) {
                await DeviceModelsService.update(model);
                navigateTo('details', id);
            } else if (model) {
                const newId = await DeviceModelsService.create(model);
                navigateTo('details', newId);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the device model with id: ${form.id}`, error);
            toast.error(t('messages.error_load_info'));
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('config_devices.models.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('config_devices.models.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
                <Form onSubmit={onSubmit} noValidate validated={validated} key={refresh}>
                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>{t('config_devices.models.list.name')}{!isDetails ? '*' : ''}</Form.Label>
                                <Form.Control required name="name" type="text" defaultValue={item?.name} readOnly={isDetails} onChange={handleChange} placeholder={t('config_devices.models.list.name')} />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3" controlId="deviceBrandId">
                                <Form.Label>{t('config_devices.models.list.brand')}{!isDetails ? '*' : ''}</Form.Label>
                                <ReadOnlySelect
                                    readOnly={isDetails}
                                    name="deviceBrandId"
                                    value={item?.deviceBrandId}
                                    options={brands}
                                    placeholder={t('common.select_option') ?? ''}
                                    required
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3" controlId="hasCanReading">
                                <Form.Check
                                    name="hasCanReading"
                                    type="switch"
                                    label={t('config_devices.models.list.has_can_reading')}
                                    checked={formData.hasCanReading}
                                    disabled={isDetails}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setFormData({ ...formData, hasCanReading: e.target.checked });
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-3" controlId="supportsIdentificationTag">
                                <Form.Check
                                    name="supportsIdentificationTag"
                                    type="switch"
                                    label={t('config_devices.models.list.supports_identification_tag')}
                                    checked={formData.supportsIdentificationTag}
                                    disabled={isDetails}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setFormData({ ...formData, supportsIdentificationTag: e.target.checked });
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className={styles.buttonsContainer}>
                        <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                            {t('common.cancel')}
                        </Button>
                        {isDetails && hasDeviceModelsWritePolicy &&
                            <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as DeviceModelDto)} >
                                {t('common.delete')}
                            </Button>
                        }
                        {isDetails && hasDeviceModelsWritePolicy &&
                            <Button variant="warning" type="button" className={styles.button} onClick={() => navigateTo('edit', item?.id)}>
                                {t('common.edit')}
                            </Button>
                        }
                        {!isDetails && hasDeviceModelsWritePolicy &&
                            <Button variant="primary" type="submit" className={styles.button}>
                                {t('common.save')}
                            </Button>
                        }
                    </div>
                </Form>
            </div>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default DeviceModelScreen;
